// StepInfra — step 6 of the wizard. A grid of infrastructure/stack cards
// (AWS, Kubernetes, Serverless, SQL, …) the user can toggle. Also echoes the
// auto-detected project shape at the bottom for confirmation.
//
// UI-ONLY: selections live in form.infraSelections, persist in the local
// draft, but are NOT sent to the backend intake payload (see the "UI-ONLY"
// marker in ../types.ts). Note the deployment fields that DO reach the
// backend are the "Project Architecture" selects on the Delivery step, not
// these cards.
import type { ComponentType } from 'react'
import {
  Cloud,
  Server,
  Network,
  Zap,
  Boxes,
  Activity,
  Database,
  LayoutGrid,
  GitBranch,
} from 'lucide-react'

import type { Detected, NewProjectForm, UpdateForm } from '../types'

const STACK: Array<{ title: string; meta: string; Icon: ComponentType<{ size?: number; className?: string }> }> = [
  { title: 'AWS', meta: 'Public Cloud', Icon: Cloud },
  { title: 'Azure', meta: 'Public Cloud', Icon: Cloud },
  { title: 'Google Cloud', meta: 'Public Cloud', Icon: Cloud },
  { title: 'On-prem / Private', meta: 'Data Center', Icon: Server },
  { title: 'Kubernetes / K8s', meta: 'Orchestrated', Icon: Network },
  { title: 'Serverless', meta: 'Event-Driven', Icon: Zap },
  { title: 'Microservices', meta: 'Distributed', Icon: LayoutGrid },
  { title: 'Event Streaming', meta: 'Kafka / NATS', Icon: Activity },
  { title: 'SQL / Relational', meta: 'Database', Icon: Database },
  { title: 'NoSQL / Document', meta: 'Database', Icon: Boxes },
]

export function StepInfra({
  form,
  update,
  detected,
}: {
  form: NewProjectForm
  update: UpdateForm
  detected: Detected
}) {
  const toggle = (title: string) => {
    const has = form.infraSelections.includes(title)
    update({
      infraSelections: has
        ? form.infraSelections.filter((c) => c !== title)
        : [...form.infraSelections, title],
    })
  }

  return (
    <>
      <header className="step-header">
        <div className="step-section-tag">
          <GitBranch size={14} />
          Engineering
        </div>
        <h1 className="step-h1">What's the <em>infrastructure</em> stack?</h1>
        <p className="step-lede">
          Select the cloud services and architectural patterns that define this environment. This
          informs security scanning and compliance posture in the{' '}
          <span className="accent">requirements package</span>.
        </p>
      </header>

      <div className="archetype-grid">
        {STACK.map(({ title, meta, Icon }) => (
          <button
            key={title}
            type="button"
            className={`archetype-card${form.infraSelections.includes(title) ? ' selected' : ''}`}
            onClick={() => toggle(title)}
          >
            <div className="archetype-card-head">
              <Icon size={26} className="archetype-icon" />
              <div className="archetype-check">✓</div>
            </div>
            <div className="archetype-title">{title}</div>
            <div className="archetype-meta">{meta}</div>
          </button>
        ))}
      </div>

      {detected.shape.shape && (
        <section className="detected-panel">
          <div className="detected-eyebrow">Detected from your description</div>
          <div className="detected-row">
            <span className="detected-row-label">Shape <span className="arrow">→</span></span>
            <span className="detected-key">{detected.shape.shape.label}</span>
            <span className="detected-confidence">
              <b>{Math.round(detected.shape.score * 100)}% confidence</b> · based on your Describe step
            </span>
          </div>
        </section>
      )}
    </>
  )
}
